<template>
  <ax-dialog
    ref="dialog"
    :title="`Editing well ${well_api} features`"
    subtitle="Edit well features to generate new predictions and update the decline parameters for this well."
    icon="mdi-pencil-outline"
    tooltip="Edit well features"
    size="large"
    persistant
    :disabled="disabled"
    @submitted="onSubmit"
    @shown="onShow"
  >
    <template slot="content">
      <v-card-text v-if="objects && floats && features">
        <v-row align="start">
          <v-col cols="2">
            <v-menu
              v-model="menu"
              :close-on-content-click="false"
              transition="scale-transition"
              offset-y
              max-width="290px"
              min-width="auto"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  v-model="startDate"
                  label="Start Date"
                  readonly
                  v-bind="attrs"
                  v-on="on"
                ></v-text-field>
              </template>
              <v-date-picker v-model="startDate" no-title @input="menu=false" />
            </v-menu>

          </v-col>
          <v-col v-if="!isEmpty(objects)" cols="4">
            <v-autocomplete 
              v-for="(options, feature, f) in objects" :key="f"
              :label="`${labels[feature]}${units(labels[feature])}`"
              v-model="features[feature]"
              :items="options.list.filter(n => n).sort()"
              clearable
            />
          </v-col>
          <v-col v-if="!isEmpty(floats)">
            <template v-for="(options, feature, f) in floats">
              <v-slider
                :key="f"
                v-if="![item.config.latitude, item.config.longitude].includes(feature)"
                :label="`${labels[feature]}${units(labels[feature])}`"
                :min="uniques[feature].min"
                :max="uniques[feature].max"
                v-model.number="features[feature]"
              >
                <template v-slot:append>
                  <v-text-field
                    v-model.number="features[feature]"
                    class="mt-0 pt-0"
                    style="width:80px"
                    hide-details dense
                    type="number"
                  />
                </template>
              </v-slider>
            </template>
          </v-col>
          <v-col v-if="!isEmpty(ints)">
            <template v-for="(options, feature, f) in ints">
              <v-text-field
                v-if="![item.config.latitude, item.config.longitude].includes(feature)"
                :key="f"
                :label="`${labels[feature]}${units(labels[feature])}`"
                v-model.number="features[feature]"
                clearable
              />
            </template>
          </v-col>
        </v-row>
      </v-card-text>
    </template>
    <template v-slot:submit_text>
      Save
    </template>
  </ax-dialog>
</template>
<script>
import AxDialog from "@/components/dialog"
import DucsPermits from '@/api/ducspermits'
import VuePlotly from '@statnett/vue-plotly'
import * as F from "@/modules/forecast"
export default {
  name: "dlgEditWell",
  props: {
    item: { type: Object, required: true },
    well: { type: Object, required: true },
    uniques: { type: Object, required: true },
    disabled: { type: Boolean, default: false }
  },
  components: {
    AxDialog, VuePlotly
  },
  data() {
    return {
      tabs: {
        selected: null,
        list: ['Forecast', 'Features']
      },
      predictions: null,
      params: null,
      features: null,
      decline: [],
      duration: {
        min: 0, 
        max: 600, 
        value: 600
      },
      target: 'oil',
      method: 'multi3a',
      menu: false,
      startDate: null,
      profile: {
        data: [],
        layout: {
          autosize: true,
          height: 250,
          legend: { x: 1, y: 1, xanchor: "right" },
          font: { family: "Roboto, sans-serif", color: '#808080' },
          hovermode: "closest",
          margin: { l: 70, t: 15, r: 15, b: 45 },
          xaxis: { title: { text: `Time (months)` }, dtick: 12 },
          yaxis: { title: { text: `Predicted Production` }, type: "log" },
          paper_bgcolor: "rgba(0,0,0,0)",
          plot_bgcolor: "rgba(0,0,0,0)"
        },
        options: { displayModeBar: false, responsive: true }
      }
    }
  },
  computed: {
    well_api() {
      return this.well[this.item.config.api]
    },
    targets() {
      return Object.keys(this.item.config.targets)
    },
    methods() {
      return this.item.methods.map(m => m.slug)
    },
    objects() {
      return Object.fromEntries(Object.entries(this.uniques).filter(([key,value]) => value.type == 'object'))
    },
    floats() {
      return Object.fromEntries(Object.entries(this.uniques).filter(([key,value]) => ["float8","float16","float32","float64"].includes(value.type)))
    },
    ints() {
      return Object.fromEntries(Object.entries(this.uniques).filter(([key,value]) => ["int8","int16","int32","int64"].includes(value.type)))
    },
    eur() {
      if (this.params) {
        return this.sum(this.decline)
      }
      return 0
    },
    labels() {
      let FEATURES = this.item.config.features
      let FIELDS = Object.fromEntries(Object.entries(this.item.methods[0].fields).filter(([k,v]) => Object.keys(FEATURES).includes(v)))
      let LABELS = {}
      Object.entries(FIELDS).forEach(([k,v]) => {
          LABELS[k] = FEATURES[v]
      })
      LABELS = Object.fromEntries(Object.entries(LABELS).map(([k,v]) => [v, k]))
      return LABELS
    }
  },
  methods: {
    onShow(visible) {
      this.profile.data = []
      this.startDate = this.well['StartDate']
      if (visible) {
        DucsPermits.getWell(this.item.uuid, this.well_api, { frame: 'features' }).then(response => {
          this.features = JSON.parse(response.data)[0]
        })
      }
    },
    onSubmit() {
      let patchPayload = {
        target: this.target,
        method: this.method,
        action: 'features',
        start: this.startDate
      }
      let predictionPayload = {
        target: this.target,
        method: this.method,
        features: this.features
      }
      Promise.all([
        DucsPermits.patch(this.item.uuid, this.well_api, patchPayload),
        DucsPermits.predict(this.item.uuid, this.well_api, predictionPayload)
      ]).then(function([patch, predictions]) {
        this.$refs.dialog.show = false
        this.$emit("submitted", patchPayload, predictions.data)
      }.bind(this))
    },
    units(text) {
      const amount = /amount/ig
      const length = /length|depth/ig
      if (text.match(amount)) return ' (lbs)'
      if (text.match(length)) return ' (ft)'
      return ''
    }
  },
}
</script>